/** Reportes del administrador: cobrado, producción por odontólogo y procedimiento, y saldos por cobrar. */
import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { dinero, rangoPeriodo, reporteCSV, NOMBRE_PERIODO, type Periodo, type Reporte } from "../../../dominio/src/index.ts";
import { AvisoError, hoy, Seccion, useAccion } from "./comun.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";

const PERIODOS: Periodo[] = ["hoy", "semana", "mes", "mes_pasado", "anio"];

/** Barras de ingresos (una serie: el título la nombra, sin leyenda). Pasa el cursor o toca una barra para ver el valor. */
function BarrasIngresos({ serie, agrupado }: { serie: Reporte["ingresos"]["serie"]; agrupado: "dia" | "mes" }) {
  const [sel, setSel] = useState<number | null>(null);
  if (!serie.length) return null;
  const max = Math.max(...serie.map((s) => s.total), 1);
  const W = 720, H = 180, izq = 48, abajo = 22, arriba = 8;
  const ancho = (W - izq) / serie.length, barra = Math.max(2, ancho - 2);
  const paso = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000].map((x) => x * 100).find((x) => max / x <= 4) ?? max / 4;
  const marcas = Array.from({ length: Math.floor(max / paso) + 1 }, (_, i) => i * paso);
  const y = (v: number) => H - abajo - (v / Math.max(max, marcas[marcas.length - 1])) * (H - abajo - arriba);
  const cadaEtiqueta = Math.ceil(serie.length / 10);
  const s = sel === null ? null : serie[sel];
  return (
    <div className="grafico" onMouseLeave={() => setSel(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Ingresos por ${agrupado === "dia" ? "día" : "mes"}`}>
        {marcas.map((m) => (
          <g key={m}>
            <line x1={izq} x2={W} y1={y(m)} y2={y(m)} stroke="var(--line)" strokeWidth={1} />
            <text x={izq - 6} y={y(m) + 4} textAnchor="end" className="eje">{m >= 100000 ? `$${m / 100000}k` : `$${m / 100}`}</text>
          </g>
        ))}
        {serie.map((d, i) => {
          const x = izq + i * ancho + 1, h = H - abajo - y(d.total);
          return (
            <g key={d.desde} onMouseEnter={() => setSel(i)} onClick={() => setSel(i)}>
              <rect x={izq + i * ancho} y={arriba} width={ancho} height={H - abajo - arriba} fill="transparent" />
              {d.total > 0 && (() => {
                const base = H - abajo, top = base - h, rr = Math.min(4, h, barra / 2);
                return <path d={`M${x} ${base}V${top + rr}Q${x} ${top} ${x + rr} ${top}H${x + barra - rr}Q${x + barra} ${top} ${x + barra} ${top + rr}V${base}Z`} fill="var(--accent)" opacity={sel === null || sel === i ? 1 : 0.55} />;
              })()}
              {i % cadaEtiqueta === 0 && <text x={x + barra / 2} y={H - 6} textAnchor="middle" className="eje">{d.etiqueta}</text>}
            </g>
          );
        })}
      </svg>
      <div className="grafico-dato" aria-live="polite">{s ? <><b>{s.etiqueta}</b>: {dinero(s.total)}</> : <span className="muted">Pasa el cursor o toca una barra para ver el valor.</span>}</div>
    </div>
  );
}

/** Tabla con barra de proporción (la barra acompaña al número, no lo reemplaza). */
function TablaBarras({ filas, titulo, col }: { filas: { k: string; det?: string; total: number; cantidad: number }[]; titulo: string; col: string }) {
  const max = Math.max(...filas.map((f) => f.total), 1);
  if (!filas.length) return <p className="muted" style={{ margin: 0 }}>Sin datos en este período.</p>;
  return (
    <table className="tabla-rep" aria-label={titulo}>
      <thead><tr><th>{col}</th><th className="num">Cant.</th><th className="num">Total</th></tr></thead>
      <tbody>{filas.map((f) => (
        <tr key={f.k}>
          <td><div>{f.det ?? f.k}</div><div className="barra-prop"><i style={{ width: `${(f.total / max) * 100}%` }} /></div></td>
          <td className="num mono">{f.cantidad}</td><td className="num mono">{dinero(f.total)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

export function Reportes() {
  const [periodo, setPeriodo] = useState<Periodo | "otro">("mes");
  const [rango, setRango] = useState(() => rangoPeriodo("mes", hoy()));
  const [r, setR] = useState<Reporte | null>(null);
  const a = useAccion();

  useEffect(() => {
    let vivo = true;
    a.correr(() => api<Reporte>("GET", `/api/reportes?desde=${rango.desde}&hasta=${rango.hasta}`)).then((x) => { if (vivo && x) setR(x); });
    return () => { vivo = false; };
  }, [rango.desde, rango.hasta]);

  useEffect(() => { document.body.classList.add("modo-reportes"); return () => document.body.classList.remove("modo-reportes"); }, []);
  const elegir = (p: Periodo) => { setPeriodo(p); setRango(rangoPeriodo(p, hoy())); };
  function descargar() {
    if (!r) return;
    const url = URL.createObjectURL(new Blob([reporteCSV(r, dinero)], { type: "text/csv;charset=utf-8" }));
    const enlace = document.createElement("a");
    enlace.href = url; enlace.download = `reporte-donna-${r.desde}-a-${r.hasta}.csv`; enlace.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div className="reportes">
      <Seccion titulo="Reportes" acciones={r && <div className="fila"><button className="btn chico" onClick={descargar}>Descargar para Excel</button><button className="btn chico" onClick={() => window.print()}>Imprimir</button></div>}>
        <div className="filtros-rep" role="group" aria-label="Período">
          <div className="seg">{PERIODOS.map((p) => <button key={p} type="button" aria-pressed={periodo === p} onClick={() => elegir(p)}>{NOMBRE_PERIODO[p]}</button>)}</div>
          <label className="campo">Desde<FechaInput value={rango.desde} max={rango.hasta} onChange={(e) => { setPeriodo("otro"); setRango({ ...rango, desde: e.target.value }); }} /></label>
          <label className="campo">Hasta<FechaInput value={rango.hasta} min={rango.desde} onChange={(e) => { setPeriodo("otro"); setRango({ ...rango, hasta: e.target.value }); }} /></label>
        </div>
        <AvisoError error={a.error} />
        {!r && !a.error && <p className="muted">Calculando…</p>}
      </Seccion>

      {r && (
        <>
          <section className="tiles" aria-label="Resumen">
            <div className="tile"><span>Cobrado</span><b>{dinero(r.ingresos.total)}</b><small>{r.ingresos.cantidad} cobro{r.ingresos.cantidad === 1 ? "" : "s"} · promedio {dinero(r.ingresos.ticketPromedio)}{r.ingresos.membresias.cantidad > 0 && ` · incluye ${dinero(r.ingresos.membresias.total)} en membresías (${r.ingresos.membresias.cantidad})`}</small></div>
            <div className="tile"><span>Producción (realizado)</span><b>{dinero(r.produccion.total)}</b><small>{r.produccion.cantidad} procedimiento{r.produccion.cantidad === 1 ? "" : "s"}, sin IVA</small></div>
            <div className="tile"><span>Por cobrar hoy</span><b>{dinero(r.porCobrar.total)}</b><small>{r.porCobrar.pacientes} paciente{r.porCobrar.pacientes === 1 ? "" : "s"} con saldo</small></div>
            <div className="tile"><span>Anulados</span><b>{r.ingresos.anulados.cantidad}</b><small>{dinero(r.ingresos.anulados.total)} en recibos anulados</small></div>
          </section>

          <Seccion titulo={`Ingresos por ${r.ingresos.agrupado === "dia" ? "día" : "mes"}`}>
            <BarrasIngresos serie={r.ingresos.serie} agrupado={r.ingresos.agrupado} />
            <details><summary className="muted">Ver como tabla</summary>
              <table className="tabla-rep"><thead><tr><th>{r.ingresos.agrupado === "dia" ? "Día" : "Mes"}</th><th className="num">Total</th></tr></thead>
                <tbody>{r.ingresos.serie.filter((s) => s.total > 0).map((s) => <tr key={s.desde}><td>{s.etiqueta}</td><td className="num mono">{dinero(s.total)}</td></tr>)}</tbody></table>
            </details>
          </Seccion>

          <div className="dos">
            <Seccion titulo="Por forma de pago">
              <TablaBarras titulo="Ingresos por forma de pago" col="Forma de pago" filas={r.ingresos.porMetodo.map((m) => ({ k: m.metodo, total: m.total, cantidad: m.cantidad }))} />
            </Seccion>
            <Seccion titulo="Producción por odontólogo">
              <TablaBarras titulo="Producción por odontólogo" col="Profesional" filas={r.produccion.porProfesional.map((m) => ({ k: m.profesional, total: m.total, cantidad: m.cantidad }))} />
            </Seccion>
          </div>

          <Seccion titulo="Procedimientos más realizados">
            <TablaBarras titulo="Procedimientos más realizados" col="Procedimiento" filas={r.produccion.porProcedimiento.map((m) => ({ k: m.codigo, det: `${m.descripcion} (${m.codigo})`, total: m.total, cantidad: m.cantidad }))} />
          </Seccion>

          <Seccion titulo="Saldos por cobrar (a hoy)">
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Lo realizado (con IVA) menos lo pagado por cada paciente, sin importar el período elegido.{r.porCobrar.aFavor > 0 ? ` Además hay ${dinero(r.porCobrar.aFavor)} abonados por adelantado.` : ""}</p>
            {r.porCobrar.lista.length ? (
              <div style={{ overflowX: "auto" }}>
                <table className="tabla-rep" aria-label="Saldos por cobrar">
                  <thead><tr><th>Paciente</th><th>Celular</th><th className="num">Realizado</th><th className="num">Pagado</th><th className="num">Saldo</th><th>Último pago</th></tr></thead>
                  <tbody>{r.porCobrar.lista.map((c) => (
                    <tr key={c.pacienteId}>
                      <td><a href={`#/paciente/${c.pacienteId}/plan`}>{c.paciente}</a> <span className="muted mono">HC {c.hc}</span></td>
                      <td className="mono">{c.telefono}</td><td className="num mono">{dinero(c.realizado)}</td><td className="num mono">{dinero(c.pagado)}</td>
                      <td className="num mono"><b>{dinero(c.saldo)}</b></td><td>{c.ultimoPago ?? "Nunca"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <p className="muted" style={{ margin: 0 }}>Nadie tiene saldo pendiente.</p>}
          </Seccion>
        </>
      )}
    </div>
  );
}
